import { useState, useEffect, useCallback } from "react";
import {
  ordersService,
  usersService,
  paymentConfirmationsService,
  siesaIntegrationLogsService,
  systemVariablesService,
} from "../../../shared/services";

const SIESA_ORDER_CUTOFF_KEY = "siesa_order_cutoff_time";
const DEFAULT_CUTOFF = "19:00";

interface SyncCounts {
  pending: number;
  synced: number;
  failed: number;
  not_required: number;
  total: number;
}

interface EntityStats {
  orders: SyncCounts;
  users: SyncCounts;
  payments: SyncCounts;
}

export const useSiesaMonitoring = () => {
  const [stats, setStats] = useState<EntityStats | null>(null);
  const [logs, setLogs] = useState<any[]>([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [logsPage, setLogsPage] = useState(1);
  const [logsFilter, setLogsFilter] = useState<{
    endpoint?: string;
    success?: boolean;
  }>({});
  const [cutoffTime, setCutoffTime] = useState(DEFAULT_CUTOFF);
  const [cutoffId, setCutoffId] = useState<number | null>(null);
  const [savingCutoff, setSavingCutoff] = useState(false);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const [ordersRes, usersRes, paymentsRes] = await Promise.all([
        ordersService.find({
          query: {
            $limit: 0,
            siesa_sync_status: { $ne: null },
            $select: ["id"],
          },
        }),
        usersService.find({
          query: {
            $limit: 0,
            siesa_sync_status: { $ne: null },
            $select: ["id"],
          },
        }),
        paymentConfirmationsService.find({
          query: {
            $limit: 0,
            siesa_sync_status: { $ne: null },
            $select: ["id"],
          },
        }),
      ]) as any[];

      const getCountByStatus = async (
        service: any,
        status: string
      ): Promise<number> => {
        const res = await service.find({
          query: { siesa_sync_status: status, $limit: 0 },
        });
        return res.total || 0;
      };

      const [
        ordersPending,
        ordersSynced,
        ordersFailed,
        ordersNotReq,
        usersPending,
        usersSynced,
        usersFailed,
        usersNotReq,
        paymentsPending,
        paymentsSynced,
        paymentsFailed,
        paymentsNotReq,
      ] = await Promise.all([
        getCountByStatus(ordersService, "pending"),
        getCountByStatus(ordersService, "synced"),
        getCountByStatus(ordersService, "failed"),
        getCountByStatus(ordersService, "not_required"),
        getCountByStatus(usersService, "pending"),
        getCountByStatus(usersService, "synced"),
        getCountByStatus(usersService, "failed"),
        getCountByStatus(usersService, "not_required"),
        getCountByStatus(paymentConfirmationsService, "pending"),
        getCountByStatus(paymentConfirmationsService, "synced"),
        getCountByStatus(paymentConfirmationsService, "failed"),
        getCountByStatus(paymentConfirmationsService, "not_required"),
      ]);

      setStats({
        orders: {
          pending: ordersPending,
          synced: ordersSynced,
          failed: ordersFailed,
          not_required: ordersNotReq,
          total: ordersRes.total || 0,
        },
        users: {
          pending: usersPending,
          synced: usersSynced,
          failed: usersFailed,
          not_required: usersNotReq,
          total: usersRes.total || 0,
        },
        payments: {
          pending: paymentsPending,
          synced: paymentsSynced,
          failed: paymentsFailed,
          not_required: paymentsNotReq,
          total: paymentsRes.total || 0,
        },
      });
    } catch (error) {
      console.error("[SiesaMonitoring] Error fetching stats", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCutoff = useCallback(async () => {
    try {
      const res = await systemVariablesService.find({
        query: {
          key: SIESA_ORDER_CUTOFF_KEY,
          deletedAt: null,
          $limit: 1,
        },
      });
      const row = res.data?.[0];
      if (row?.value) {
        setCutoffTime(row.value);
        setCutoffId(row.id);
      }
    } catch (error) {
      console.error("[SiesaMonitoring] Error fetching cutoff", error);
    }
  }, []);

  const saveCutoff = useCallback(async (value: string) => {
    setSavingCutoff(true);
    try {
      if (cutoffId) {
        await systemVariablesService.patch(cutoffId, { value });
      } else {
        const created = await systemVariablesService.create({
          name: "Hora máxima de pedidos a Siesa el último día del mes",
          key: SIESA_ORDER_CUTOFF_KEY,
          value,
        });
        setCutoffId(created?.id ?? null);
      }
      setCutoffTime(value);
    } finally {
      setSavingCutoff(false);
    }
  }, [cutoffId]);

  const fetchLogs = useCallback(async () => {
    try {
      const query: any = {
        $limit: 20,
        $skip: (logsPage - 1) * 20,
        $sort: { createdAt: -1 },
      };

      if (logsFilter.endpoint) {
        query.endpoint = logsFilter.endpoint;
      }
      if (logsFilter.success !== undefined) {
        query.success = logsFilter.success;
      }

      const res = await siesaIntegrationLogsService.find({ query });
      setLogs(res.data || []);
      setLogsTotal(res.total || 0);
    } catch (error) {
      console.error("[SiesaMonitoring] Error fetching logs", error);
    }
  }, [logsPage, logsFilter]);

  useEffect(() => {
    fetchStats();
    fetchCutoff();
  }, [fetchStats, fetchCutoff]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  return {
    stats,
    logs,
    logsTotal,
    loading,
    logsPage,
    setLogsPage,
    logsFilter,
    setLogsFilter,
    cutoffTime,
    savingCutoff,
    saveCutoff,
    refresh: () => {
      fetchStats();
      fetchLogs();
    },
  };
};
